import { useState, type ChangeEvent } from 'react';
import { Upload, File } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';

interface FileUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (file: File | null, url: string, mediaName: string) => void;
  initialFile: File | null;
  initialUrl: string;
  initialMediaName: string;
}

export function ProjectUploadModal({
  open,
  onOpenChange,
  onSubmit,
  initialFile,
  initialUrl,
  initialMediaName
}: FileUploadModalProps) {
  const [fileName, setFileName] = useState(initialMediaName);
  const [file, setFile] = useState<File | null>(initialFile);
  const [fileUrl, setFileUrl] = useState(initialUrl);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      setFileName(selectedFile.name.split('.')[0]);
      setFileUrl(''); // Clear URL when file is selected
    }
  };

  const handleSave = () => {
    if (!fileName.trim()) return;
    onSubmit(file, fileUrl, fileName);
    onOpenChange(false);
  };

  const handleRemoveFile = () => {
    setFile(null);
    setFileName('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='font-grotesk sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle className='font-grotesk text-xl font-bold'>
            Add Project Media
          </DialogTitle>
          <DialogDescription className='mt-2 text-sm text-[#344054]'>
            Upload a media file or provide a URL link
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-6 py-4'>
          <div className='space-y-2'>
            <h3 className='font-medium'>
              Media title <span className='text-destructive'>*</span>
            </h3>
            <Input
              placeholder='Enter a title for your media'
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
            />
          </div>

          <div className='space-y-2 rounded-lg border-2 border-dashed p-10 text-center'>
            <div className='flex justify-center'>
              <div className='rounded-lg bg-blue-100 p-4'>
                <Upload className='h-8 w-8 text-blue-500' />
              </div>
            </div>
            <h3 className='font-medium text-[#19191B]'>
              {file ? 'Media file selected' : 'Select a file to upload'}
            </h3>
            <p className='text-[#718096]'>
              {file
                ? file.name
                : 'Supported formats: JPG, PNG, PDF, MP4, or drag and drop here'}
            </p>
            {file && (
              <div className='mt-2 flex items-center justify-center gap-2'>
                <File className='h-4 w-4' />
                <span className='text-sm'>{file.name}</span>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={handleRemoveFile}
                  className='text-red-500'
                >
                  Remove
                </Button>
              </div>
            )}
            <Button
              variant='outline'
              className='mt-4'
              onClick={() => document.getElementById('file-upload')?.click()}
            >
              {file ? 'Change File' : 'Select File'}
            </Button>
            <input
              id='file-upload'
              type='file'
              className='hidden'
              onChange={handleFileChange}
              accept='.jpg,.jpeg,.png,.pdf,.mp4,.mov,.csv,.xlsx,.docx'
            />
          </div>

          <div className='relative'>
            <div className='relative flex justify-center text-xs uppercase'>
              <span className='bg-background px-2 text-[#878789]'>OR</span>
            </div>
          </div>

          <div className='space-y-2'>
            <h3 className='font-semibold text-[#111014]'>Media URL</h3>
            <Input
              placeholder='Enter media URL (e.g., https://example.com/media.mp4)'
              value={fileUrl}
              onChange={(e) => {
                setFileUrl(e.target.value);
                if (e.target.value) setFile(null); // Clear file when URL is entered
              }}
            />
          </div>
        </div>

        <div className='flex justify-between'>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            className='w-[48%]'
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className='w-[48%]'
            disabled={(!file && !fileUrl) || !fileName.trim()}
          >
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
